import { useEffect, useState } from 'react';

const SESSION_KEY = 'show-build-info';
const EVENT_NAME = 'build-info-visibility-changed';
const TRUE_VALUES = new Set(['1', 'true', 'yes', 'on']);
const FALSE_VALUES = new Set(['0', 'false', 'no', 'off']);
const DEV_BUILD_INFO_HOSTS = new Set([
  'logicv-teams-cdr-dev.azurewebsites.net',
  'dev-app.voiceiq.logicv.com',
]);

function getQueryFlagValue(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  for (const key of ['showBuild', 'debugBuild', 'version']) {
    const value = params.get(key);
    if (value !== null) {
      return value.toLowerCase();
    }
  }

  return null;
}

function isDevBuildInfoHost(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return DEV_BUILD_INFO_HOSTS.has(window.location.host.toLowerCase());
}

export function shouldShowBuildInfo(): boolean {
  if (__SHOW_BUILD_INFO__) {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const queryFlag = getQueryFlagValue();
  if (queryFlag && TRUE_VALUES.has(queryFlag)) {
    window.sessionStorage.setItem(SESSION_KEY, '1');
    return true;
  }

  if (queryFlag && FALSE_VALUES.has(queryFlag)) {
    window.sessionStorage.setItem(SESSION_KEY, '0');
    return false;
  }

  const sessionValue = window.sessionStorage.getItem(SESSION_KEY);
  if (sessionValue === '1') {
    return true;
  }

  if (sessionValue === '0') {
    return false;
  }

  return isDevBuildInfoHost();
}

function emitVisibilityChange() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(EVENT_NAME));
  }
}

export function setBuildInfoVisibility(visible: boolean) {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(SESSION_KEY, visible ? '1' : '0');

  emitVisibilityChange();
}

export function toggleBuildInfoVisibility(): boolean {
  const nextValue = !shouldShowBuildInfo();
  setBuildInfoVisibility(nextValue);
  return nextValue;
}

export function useBuildInfoVisibility(locationSearch?: string) {
  const [showBuildInfo, setShowBuildInfo] = useState(() => shouldShowBuildInfo());

  useEffect(() => {
    const syncVisibility = () => {
      setShowBuildInfo(shouldShowBuildInfo());
    };

    syncVisibility();
    window.addEventListener(EVENT_NAME, syncVisibility);
    window.addEventListener('storage', syncVisibility);

    return () => {
      window.removeEventListener(EVENT_NAME, syncVisibility);
      window.removeEventListener('storage', syncVisibility);
    };
  }, [locationSearch]);

  return showBuildInfo;
}
